import React, { useState, useEffect, useRef } from 'react';
import { Star, ExternalLink, Loader2 } from 'lucide-react';
import { Button } from './ui/button';
import { setOptions, importLibrary } from '@googlemaps/js-api-loader';

const GOOGLE_API_KEY = process.env.REACT_APP_GOOGLE_API_KEY;
const GOOGLE_REVIEW_URL = 'https://www.google.com/search?q=binary-consultancy';

const GoogleReviews = () => {
  const [reviews, setReviews] = useState([]);
  const [rating, setRating] = useState(0);
  const [totalReviews, setTotalReviews] = useState(16);
  const [placeId, setPlaceId] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const initAttempted = useRef(false);

  useEffect(() => {
    if (initAttempted.current) return;
    initAttempted.current = true;

    const fetchReviews = async () => {
      try {
        // Step 1: Find the place ID using JS SDK
        setOptions({ key: GOOGLE_API_KEY, v: 'weekly' });
        const { Place } = await importLibrary('places');

        const { places } = await Place.searchByText({
          textQuery: 'Binary Consultancy 126 High Street West Glossop',
          fields: ['id', 'displayName', 'rating', 'userRatingCount', 'reviews'],
          maxResultCount: 1,
        });

        if (!places || places.length === 0) {
          setError(true);
          return;
        }

        const place = places[0];
        setRating(place.rating || 0);
        setTotalReviews(place.userRatingCount || 16);
        if (place.id) setPlaceId(place.id);

        if (place.reviews?.length > 0) {
          // Sort by newest first (from the reviews Google provides)
          const sorted = [...place.reviews].sort((a, b) => {
            const timeA = a.publishTime ? new Date(a.publishTime).getTime() : 0;
            const timeB = b.publishTime ? new Date(b.publishTime).getTime() : 0;
            return timeB - timeA;
          });
          setReviews(sorted.slice(0, 3));
        }
      } catch (e) {
        setError(true);
      } finally {
        setLoading(false);
      }
    };

    fetchReviews();
  }, []);

  const timeAgo = (dateStr) => {
    if (!dateStr) return '';
    const seconds = Math.floor((Date.now() - new Date(dateStr).getTime()) / 1000);
    const intervals = [
      { label: 'year', secs: 31536000 },
      { label: 'month', secs: 2592000 },
      { label: 'week', secs: 604800 },
      { label: 'day', secs: 86400 },
    ];
    for (const { label, secs } of intervals) {
      const count = Math.floor(seconds / secs);
      if (count >= 1) return `${count} ${label}${count > 1 ? 's' : ''} ago`;
    }
    return 'Just now';
  };

  if (error) {
    // Fallback: show a simple Google reviews link section
    return (
      <section className="py-16 bg-white" data-testid="google-reviews-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="bg-gradient-to-br from-slate-950 via-slate-900 to-slate-950 rounded-3xl p-8 md:p-12 relative overflow-hidden">
            <div className="absolute top-0 right-0 w-72 h-72 bg-blue-600/10 rounded-full blur-3xl"></div>
            <div className="relative z-10 text-center py-8">
              <div className="flex items-center justify-center gap-2 mb-4">
                <svg viewBox="0 0 24 24" className="w-8 h-8" xmlns="http://www.w3.org/2000/svg">
                  <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 01-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z" fill="#4285F4"/>
                  <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/>
                  <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05"/>
                  <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"/>
                </svg>
                <span className="text-white font-semibold text-lg">Google Reviews</span>
              </div>
              <div className="flex items-center justify-center gap-1 mb-6">
                {Array.from({ length: 5 }).map((_, i) => (
                  <Star key={`fallback-star-${i}`} className="w-5 h-5 text-amber-400 fill-amber-400" />
                ))}
              </div>
              <p className="text-slate-300 mb-6">See what our customers say about us on Google</p>
              <div className="flex items-center justify-center gap-4">
                <a href={GOOGLE_REVIEW_URL} target="_blank" rel="noopener noreferrer" data-testid="google-reviews-link">
                  <Button variant="outline" className="border-slate-700 text-white hover:bg-white/10 hover:text-white gap-2">
                    View Our Reviews <ExternalLink className="w-4 h-4" />
                  </Button>
                </a>
              </div>
            </div>
          </div>
        </div>
      </section>
    );
  }

  return (
    <section className="py-16 bg-white" data-testid="google-reviews-section">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="bg-gradient-to-br from-slate-950 via-slate-900 to-slate-950 rounded-3xl p-8 md:p-12 relative overflow-hidden">
          <div className="absolute top-0 right-0 w-72 h-72 bg-blue-600/10 rounded-full blur-3xl"></div>
          <div className="absolute bottom-0 left-0 w-72 h-72 bg-cyan-600/10 rounded-full blur-3xl"></div>

          <div className="relative z-10">
            {/* Header */}
            <div className="flex flex-col md:flex-row items-center justify-between gap-6 mb-10">
              <div className="flex items-center gap-4">
                <div className="flex items-center gap-2">
                  <svg viewBox="0 0 24 24" className="w-8 h-8" xmlns="http://www.w3.org/2000/svg">
                    <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 01-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z" fill="#4285F4"/>
                    <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/>
                    <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05"/>
                    <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"/>
                  </svg>
                  <span className="text-white font-semibold text-lg">Google Reviews</span>
                </div>
                {!loading && rating > 0 && (
                  <div className="flex items-center gap-2 bg-white/10 px-4 py-2 rounded-full backdrop-blur-sm">
                    <span className="text-2xl font-bold text-white">{rating.toFixed(1)}</span>
                    <div className="flex gap-0.5">
                      {Array.from({ length: 5 }).map((_, i) => (
                        <Star key={`header-star-${i}`} className={`w-4 h-4 ${i < Math.round(rating) ? 'text-amber-400 fill-amber-400' : 'text-slate-600'}`} />
                      ))}
                    </div>
                    <span className="text-slate-400 text-sm">({totalReviews})</span>
                  </div>
                )}
              </div>
              <a href={GOOGLE_REVIEW_URL} target="_blank" rel="noopener noreferrer" data-testid="google-reviews-link">
                <Button variant="outline" className="border-slate-700 text-white hover:bg-white/10 hover:text-white gap-2">
                  View All on Google <ExternalLink className="w-4 h-4" />
                </Button>
              </a>
              {placeId && (
                <a
                  href={`https://search.google.com/local/writereview?placeid=${placeId}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="leave-review-link"
                >
                  <Button className="bg-blue-600 hover:bg-blue-700 text-white gap-2">
                    <Star className="w-4 h-4" /> Leave a Review
                  </Button>
                </a>
              )}
            </div>

            {/* Reviews */}
            {loading ? (
              <div className="flex items-center justify-center py-16">
                <Loader2 className="w-8 h-8 text-blue-400 animate-spin" />
                <span className="ml-3 text-slate-400">Loading reviews from Google...</span>
              </div>
            ) : reviews.length > 0 ? (
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                {reviews.map((r, i) => {
                  const authorName = r.authorAttribution?.displayName || r.originalText?.languageCode || 'Anonymous';
                  const photoUri = r.authorAttribution?.photoURI || r.authorAttribution?.photoUri || '';
                  const reviewRating = r.rating || 5;
                  const reviewText = r.text?.text || (typeof r.text === 'string' ? r.text : '') || r.originalText?.text || '';
                  const publishTime = r.publishTime || '';
                  const relativeTime = r.relativePublishTimeDescription || timeAgo(publishTime);

                  return (
                    <div
                      key={authorName + publishTime}
                      className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-xl p-6 hover:bg-white/10 transition-colors"
                      data-testid={`google-review-${i}`}
                    >
                      <div className="flex items-center gap-3 mb-3">
                        {photoUri ? (
                          <img
                            src={photoUri}
                            alt={authorName}
                            className="w-10 h-10 rounded-full object-cover"
                            referrerPolicy="no-referrer"
                          />
                        ) : (
                          <div className="w-10 h-10 bg-gradient-to-br from-blue-500 to-cyan-500 rounded-full flex items-center justify-center text-white font-bold text-sm">
                            {authorName.charAt(0)}
                          </div>
                        )}
                        <div>
                          <p className="text-white font-medium text-sm">{authorName}</p>
                          <p className="text-slate-500 text-xs">{relativeTime}</p>
                        </div>
                      </div>
                      <div className="flex gap-0.5 mb-3">
                        {Array.from({ length: 5 }).map((_, j) => (
                          <Star key={`review-star-${j}`} className={`w-3.5 h-3.5 ${j < reviewRating ? 'text-amber-400 fill-amber-400' : 'text-slate-600'}`} />
                        ))}
                      </div>
                      <p className="text-slate-300 text-sm leading-relaxed">"{reviewText}"</p>
                    </div>
                  );
                })}
              </div>
            ) : (
              <div className="text-center py-8">
                <p className="text-slate-400">Visit our Google profile to see all reviews.</p>
              </div>
            )}
          </div>
        </div>
      </div>
    </section>
  );
};

export default GoogleReviews;
